/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useContext } from 'react';
import { useLoanContext } from './LoanContext';
import {
  areDatesOnSameDay,
  epochToPST8AMDate,
  isDateBefore,
  isDateEmptyOrNull,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
} from '../common/UtilityFunctions';
import useGetEventDate from '../common/hooks/useGetEventDate';
import { ServicingPayment } from '../components/forms/LoanHistoryTransactionForm';

type LoanHistoryContextType = {
  isPaymentApplicableBasedOnMigration: (pmt: ServicingPayment, migrationEvent: EventObj) => boolean;
  getRemovedDuplicatesServPayments: (servicingLoanPayments: ServicingPayment[]) => ServicingPayment[];
};

const LoanHistoryContext = createContext<LoanHistoryContextType | undefined>(undefined);

const useLoanHistoryContext = () => {
  const loanHistoryContext = useContext(LoanHistoryContext);
  if (loanHistoryContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('LoanHistoryContext must be inside a LoanHistoryProvider');
  }
  return loanHistoryContext;
};

const LoanHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isPaymentApplicableBasedOnMigration = useCallback(
    (pmt: ServicingPayment, migrationDate: EventObj | null): boolean => {
      if (isObjEmptyOrNull(migrationDate)) {
        return true;
      }
      if (isDateEmptyOrNull(migrationDate.activeDate) || !migrationDate.active) {
        return true;
      }
      if (isDateEmptyOrNull(pmt.postDate)) {
        return false;
      }
      if (!isStrEmptyOrNull(pmt.mcdTransactionId)) {
        return true;
      }
      const postDatePST8AM = epochToPST8AMDate(pmt.postDate);
      const migrationDatePST8AM = epochToPST8AMDate(migrationDate.activeDate);
      if (isDateBefore(postDatePST8AM, migrationDatePST8AM)) {
        return false;
      }
      if (pmt.uid == 'IGNR' && areDatesOnSameDay(postDatePST8AM, migrationDatePST8AM)) {
        return false;
      }
      return true;
    },
    [],
  );

  const getRemovedDuplicatesServPayments = useCallback(
    (servicingLoanPayments: ServicingPayment[]): ServicingPayment[] => {
      const getTransactionKey = (batchNo: string, cnt: string) => `${batchNo}|${cnt}`;

      // Find transaction IDs that occur more than once
      const transactionIdCounts = new Map<string, number>();
      servicingLoanPayments.forEach((payment) => {
        if (!isStrEmptyOrNull(payment.mcdTransactionId)) {
          transactionIdCounts.set(
            payment.mcdTransactionId,
            (transactionIdCounts.get(payment.mcdTransactionId) ?? 0) + 1,
          );
        }
      });

      const duplicateTransactionIds = new Set(
        Array.from(transactionIdCounts.entries())
          .filter(([, count]) => count > 1)
          .map(([transactionId]) => transactionId),
      );

      // Build a list of batch/cnt pairs belonging to transactions that share a duplicate mcdTransactionId
      const duplicateOriginalKeys = new Set<string>();
      servicingLoanPayments.forEach((payment) => {
        if (!isStrEmptyOrNull(payment.mcdTransactionId) && duplicateTransactionIds.has(payment.mcdTransactionId)) {
          duplicateOriginalKeys.add(getTransactionKey(payment.batchNo, payment.cnt));
        }
      });

      // Find reversals belonging to those duplicate transactions
      // Note that a reversal does not have an mcdTransactionId; rvrsBatch/rvrsCnt points back to the batchNo/cnt of the transaction being reversed
      const reversedOriginalKeys = new Set<string>();
      const duplicateReversalRecIds = new Set<string>();
      servicingLoanPayments.forEach((payment) => {
        if (
          payment.principalAlloc < 0 &&
          payment.interestAlloc < 0 &&
          !isStrEmptyOrNull(payment.rvrsBatch) &&
          !isStrEmptyOrNull(payment.rvrsCnt)
        ) {
          const reversedTransactionKey = getTransactionKey(payment.rvrsBatch, payment.rvrsCnt);

          if (duplicateOriginalKeys.has(reversedTransactionKey)) {
            reversedOriginalKeys.add(reversedTransactionKey);
            duplicateReversalRecIds.add(payment.recId);
          }
        }
      });

      // Remove:
      // 1. Reversal records belonging to the duplicated transaction ID
      // 2. The original transactions that those reversals point back to
      // Unrelated reversal transactions remain untouched
      const paymentsWithoutReversedDuplicates = servicingLoanPayments.filter((payment) => {
        if (duplicateReversalRecIds.has(payment.recId)) {
          return false;
        }

        if (!isStrEmptyOrNull(payment.mcdTransactionId) && duplicateTransactionIds.has(payment.mcdTransactionId)) {
          const transactionKey = getTransactionKey(payment.batchNo, payment.cnt);

          if (reversedOriginalKeys.has(transactionKey)) {
            return false;
          }
        }

        return true;
      });

      // Preserve the existing duplicate handling in case more than one non-reversed records remains for the same mcdTransactionId
      const removedDuplicates = paymentsWithoutReversedDuplicates.reduce((accumulator: ServicingPayment[], current) => {
        if (isStrEmptyOrNull(current.mcdTransactionId)) {
          accumulator = accumulator.concat(current);
          return accumulator;
        }

        const exists = accumulator.find((item) => item.mcdTransactionId === current.mcdTransactionId);

        if (!exists) {
          accumulator = accumulator.concat(current);
        } else if ((exists.postDate, current.postDate)) {
          accumulator = accumulator.filter((item) => item.mcdTransactionId !== exists.mcdTransactionId);

          accumulator = accumulator.concat(current);
        }

        return accumulator;
      }, []);

      return removedDuplicates;
    },
    [],
  );

  return (
    <LoanHistoryContext.Provider value={{ isPaymentApplicableBasedOnMigration, getRemovedDuplicatesServPayments }}>
      {children}
    </LoanHistoryContext.Provider>
  );
};

export { LoanHistoryProvider, useLoanHistoryContext };
